import logger from '@/utils/client-logger';
import { TIKTOK_CLICK_ID_STORAGE_KEY } from '@/utils/ad-click-attribution-constants';
import { getSetLocalStorage } from '@/utils/tools';

/** 广告点击归因支持的平台集合，与任何具体埋点 Provider 实现无关。 */
export type AdClickAttributionPlatform =
  | 'facebook'
  | 'tiktok'
  | 'bing'
  | 'snapchat'
  | 'rtbHouse'
  | 'criteo';

/** 广告点击归因记录的有效天数。 */
export const AD_CLICK_ATTRIBUTION_TTL_DAYS = 400;
/** 广告点击归因记录的毫秒有效期。 */
export const AD_CLICK_ATTRIBUTION_TTL_MS = AD_CLICK_ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
/** 广告点击归因 Cookie 的秒级 Max-Age。 */
export const AD_CLICK_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS =
  AD_CLICK_ATTRIBUTION_TTL_DAYS * 24 * 60 * 60;

/** 单个平台点击值转换函数的输入。 */
export interface AdClickTransformInput {
  provider: AdClickAttributionPlatform;
  queryKey: string;
  storageKey: string;
  value: string;
  now: number;
}

/** 平台点击值的可选转换函数。 */
export type AdClickValueTransform = (input: AdClickTransformInput) => string | null | undefined;

/** 单个平台点击归因的查询与存储配置。 */
export interface AdClickAttributionConfig {
  provider: AdClickAttributionPlatform;
  queryKeys: readonly string[];
  storageKey: string;
  transform?: AdClickValueTransform;
}

/** 已解析且带有效期的广告点击归因记录。 */
export interface AdClickAttributionRecord {
  provider: AdClickAttributionPlatform;
  queryKey: string;
  storageKey: string;
  value: string;
  expireAt: number;
}

/** 创建点击归因记录时可覆盖的配置和时间来源。 */
export interface CreateAdClickAttributionRecordsOptions {
  config?: readonly AdClickAttributionConfig[];
  now?: () => number;
}

/** 从当前浏览器地址捕获点击归因时的可选参数。 */
export interface CaptureAdClickAttributionOptions extends CreateAdClickAttributionRecordsOptions {
  search?: string;
}

/** 将 Facebook click id 转换为业务要求的 fbc 格式。 */
export function transformFacebookFbc({ value, now }: AdClickTransformInput): string {
  return `fb.1.${now}.${value}`;
}

/** Z 支持的广告点击归因平台及其查询、存储映射。 */
export const AD_CLICK_ATTRIBUTION_CONFIG: readonly AdClickAttributionConfig[] = [
  {
    provider: 'facebook',
    queryKeys: ['fbclid'],
    storageKey: 'z_fb_fbc',
    transform: transformFacebookFbc,
  },
  {
    provider: 'tiktok',
    queryKeys: ['ttclid'],
    storageKey: TIKTOK_CLICK_ID_STORAGE_KEY,
  },
  {
    provider: 'bing',
    queryKeys: ['msclkid'],
    storageKey: 'z_ms_uetmclkid',
  },
  {
    provider: 'snapchat',
    queryKeys: ['ScCid', 'sccid'],
    storageKey: 'z_sp_scid',
  },
  {
    provider: 'rtbHouse',
    queryKeys: ['rtbhc'],
    storageKey: 'z_rtb_rtbhc',
  },
  {
    provider: 'criteo',
    queryKeys: ['cto_pld'],
    storageKey: 'z_cto_cto_pld',
  },
];

/** 按平台配置顺序返回第一个非空查询参数。 */
function getFirstQueryValue(
  queryParams: URLSearchParams,
  queryKeys: readonly string[],
): { queryKey: string; value: string } | null {
  for (const queryKey of queryKeys) {
    const value = queryParams.getAll(queryKey).find((item) => item.trim());
    if (value) {
      return { queryKey, value };
    }
  }
  return null;
}

/** 安全转换单条归因值，转换失败或空值时跳过当前平台。 */
function resolveRecordValue(
  config: AdClickAttributionConfig,
  queryKey: string,
  value: string,
  now: number,
): string | null {
  try {
    const transformedValue = config.transform
      ? config.transform({
          provider: config.provider,
          queryKey,
          storageKey: config.storageKey,
          value,
          now,
        })
      : value;

    if (!transformedValue || !transformedValue.trim()) {
      return null;
    }

    return transformedValue;
  } catch (error) {
    logger.error('Ad click attribution transform error:', {
      provider: config.provider,
      queryKey,
      error,
    });
    return null;
  }
}

/** 从查询串创建全部受支持且有效的广告点击归因记录。 */
export function createAdClickAttributionRecords(
  search: string,
  options: CreateAdClickAttributionRecordsOptions = {},
): AdClickAttributionRecord[] {
  const configList = options.config ?? AD_CLICK_ATTRIBUTION_CONFIG;
  const now = options.now?.() ?? Date.now();
  const expireAt = now + AD_CLICK_ATTRIBUTION_TTL_MS;
  const queryParams = new URLSearchParams(search);
  const records: AdClickAttributionRecord[] = [];

  for (const config of configList) {
    const matchedQuery = getFirstQueryValue(queryParams, config.queryKeys);
    if (!matchedQuery) continue;

    const transformedValue = resolveRecordValue(
      config,
      matchedQuery.queryKey,
      matchedQuery.value,
      now,
    );
    if (!transformedValue) continue;

    records.push({
      provider: config.provider,
      queryKey: matchedQuery.queryKey,
      storageKey: config.storageKey,
      value: transformedValue,
      expireAt,
    });
  }

  return records;
}

/** 创建带固定 Path 与 SameSite 策略的归因 Cookie 字符串。 */
export function buildAdClickCookieString(storageKey: string, value: string): string {
  return `${storageKey}=${encodeURIComponent(value)}; Max-Age=${AD_CLICK_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
}

/** 将单条归因记录同时写入业务 localStorage 与 Cookie。 */
function persistAdClickAttributionRecord(record: AdClickAttributionRecord): void {
  getSetLocalStorage.set(record.storageKey, record.value, record.expireAt);
  document.cookie = buildAdClickCookieString(record.storageKey, record.value);
}

/** 捕获当前地址中的广告点击归因，并隔离各平台持久化失败。 */
export function captureAdClickAttribution(
  options: CaptureAdClickAttributionOptions = {},
): AdClickAttributionRecord[] {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return [];
  }

  try {
    const records = createAdClickAttributionRecords(
      options.search ?? window.location.search,
      options,
    );

    records.forEach((record) => {
      try {
        persistAdClickAttributionRecord(record);
      } catch (error) {
        logger.error('Ad click attribution persist error:', {
          provider: record.provider,
          storageKey: record.storageKey,
          error,
        });
      }
    });

    return records;
  } catch (error) {
    logger.error('Ad click attribution capture error:', error);
    return [];
  }
}
